import * as React from 'react';
import * as _ from 'lodash';
import TableFilterChip from './TableFilterChip';
import {ColumnsType, ComplexCondition, FilterValuesType, FilterValueType, OperatorType} from '../redux/actions';
import Button from '@material-ui/core/Button/Button';
import AddIcon from '@material-ui/icons/Add';
import withStyles, {WithStyles} from '@material-ui/core/styles/withStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import {filter as filterIconPath, filterOutlined} from 'cl-icons';
import SvgIcon from 'cl-icons/SvgIcon';
import {Theme} from '@material-ui/core/styles/createMuiTheme';
import Menu from '@material-ui/core/Menu/Menu';
import MenuItem from '@material-ui/core/MenuItem/MenuItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import DefaultEditDialog, {DefaultEditDialogFormData} from '../DefaultEditDialog';
import {findDOMNode} from 'react-dom';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import CircularProgress from '@material-ui/core/CircularProgress';
import {ValueType} from 'cl-form/validation/validators';
import {stringToDashed} from 'cl-automation-utils';
import {getCellWithOptionsDisplayData} from '../VirtualizedHelpers';
import {FormSelectOption} from 'cl-form/utils/orderByUtils';
interface TableFilterState {
  editingColumn?: string | null;
  editingColumnAnchorEl?: HTMLElement | null;
}

export interface TableFilterProps {
  open: boolean;
  filterValues: FilterValuesType;
  columns: ColumnsType;
  count: number;
  onChange(filterValues: FilterValuesType): void;
  onClear(): void;
  preventMoreFilters?: boolean;
  onSetOpen(open: boolean);
  renderButtons?: boolean;
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      marginRight: 'auto',
      flexWrap: 'wrap',
      width: '100%',
      minHeight: theme.spacing(6),
      marginBottom: theme.spacing(2),
    },
    resultCount: {
      textAlign: 'right',
      minWidth: theme.spacing(5),
    },
    clearButton: {
      marginLeft: 'auto',
    },
  });

class TableFilter extends React.PureComponent<TableFilterProps & WithStyles<typeof styles>, TableFilterState> {
  state: TableFilterState = {};

  private clearEdit = () => {
    this.setState({editingColumn: null});
  };

  handleClearFilter = () => {
    const {onClear} = this.props;
    onClear();
  };

  handleDelete(columnName: string, onDelete?: any) {
    const {onChange, filterValues} = this.props;
    if (onDelete) {
      onDelete(filterValues);
    } else {
      onChange(_.omit(filterValues, columnName));
    }
  }

  handleEditFilter(columnName: string, onClick?: any) {
    if (onClick) {
      onClick();
    } else {
      this.setState({editingColumn: columnName, editingColumnAnchorEl: null});
    }
  }

  handleFilterEditChange = (value: ValueType, operator: OperatorType) => {
    const {onChange, filterValues} = this.props;
    const {editingColumn} = this.state;
    if (operator) {
      onChange({...filterValues, [editingColumn]: {value, operator} as ComplexCondition});
    } else {
      onChange({...filterValues, [editingColumn]: value});
    }
    this.clearEdit();
  };

  handleEditFilterRef = (ref: React.ReactInstance) =>
    this.setState({editingColumnAnchorEl: findDOMNode(ref) as HTMLElement});

  handleFilterOpen = () => this.props.onSetOpen(true);
  handleFilterClose = () => this.props.onSetOpen(false);

  private renderEditCellDialog() {
    const {filterValues, columns} = this.props;
    const {editingColumn, editingColumnAnchorEl} = this.state;
    const editingColumnInfo = columns[editingColumn];
    const value: FilterValueType = filterValues[editingColumn];
    const valueProps: DefaultEditDialogFormData =
      _.isString(value) || _.isNumber(value) || _.isArray(value) ? {value: value} : value;
    return (
      <DefaultEditDialog
        onClose={this.clearEdit}
        open={true}
        name={name}
        anchorEl={editingColumnAnchorEl}
        onChange={this.handleFilterEditChange}
        options={this.getFilterOptions(editingColumn)}
        type={editingColumnInfo.type}
        variant="value-and-operator"
        title={editingColumnInfo.displayName || editingColumn}
        orderBy={editingColumnInfo.orderBy}
        {...valueProps}
      />
    );
  }

  private getFilterOptions = _.memoize(columnName => {
    const editingColumnInfo = this.props.columns[columnName];
    if (editingColumnInfo.options) {
      const filterOptions = {};
      _.forEach(editingColumnInfo.options, ({value, label}) => {
        if (label) {
          const existingValue = filterOptions[label];
          filterOptions[label] = existingValue
            ? [...(_.isArray(existingValue) ? existingValue : [existingValue]), value]
            : value;
        } else {
          filterOptions[value] = value;
        }
      });
      return _.map(filterOptions, (value, label) => ({
        value: _.isArray(value) ? JSON.stringify(value) : value,
        label,
      })) as FormSelectOption[];
    }
    return editingColumnInfo.options;
  });

  render() {
    // noinspection JSUnusedLocalSymbols
    const {
      renderButtons,
      classes: {root, clearButton, resultCount},
      onChange,
      count,
      filterValues,
      columns,
      preventMoreFilters,
      open,
      onSetOpen,
      onClear,
      ...otherProps
    } = this.props;
    const {editingColumn, editingColumnAnchorEl} = this.state;
    const visibleColumns = _.pickBy(columns, ({filterDisabled}) => !filterDisabled);
    const withValues = _.pickBy(visibleColumns, (column, columnName) => filterValues[columnName] !== undefined);
    const recommendations = _.pickBy(
      visibleColumns,
      ({filterRecommended}, columnName) => filterValues[columnName] === undefined && filterRecommended
    );
    const {true: recommended, false: otherColumns} = _.groupBy(
      _.keys(_.omitBy(visibleColumns, ({hidden}) => hidden)),
      columnName => !!visibleColumns[columnName].filterRecommended
    );
    const editingColumnNotRendered = !withValues[editingColumn] && !recommendations[editingColumn];
    const empty = _.isEmpty(withValues);
    const clearable = _.isEmpty(_.filter(columns, ({filterRequired}) => filterRequired));
    let iconPath;
    if (!empty) {
      iconPath = filterIconPath;
    } else if (open || editingColumn) {
      iconPath = filterOutlined;
    }
    const editDialogOpen = !!(editingColumn && (editingColumnAnchorEl || editingColumnNotRendered));
    return (
      <div className={root} {...otherProps}>
        <SvgIcon id="cla-icon-filter-bar" path={iconPath} />
        <span id="cla-filter-results" className={resultCount}>
          &nbsp;{count === undefined ? <CircularProgress size={12} /> : count}&nbsp;
        </span>{' '}
        results {empty ? undefined : 'for'}&nbsp;&nbsp;
        {_.map(
          withValues,
          ({filterRequired, filterRecommended, onFilterClick, onFilterDelete, filterReadonly, displayName}, name) => {
            return (
              <TableFilterChip
                id={`cla-chip-filter-${stringToDashed(name)}`}
                key={name}
                name={displayName || name}
                value={getCellWithOptionsDisplayData(this.getFilterOptions(name), filterValues[name])}
                required={filterRequired}
                onClick={() => this.handleEditFilter(name, onFilterClick)}
                onDelete={() => this.handleDelete(name, onFilterDelete)}
                innerRef={editingColumn === name ? this.handleEditFilterRef : undefined}
                readonly={filterReadonly}
              />
            );
          }
        )}
        {editDialogOpen ? this.renderEditCellDialog() : undefined}
        {_.map(recommendations, ({filterRequired, filterRecommended, displayName}, name) => {
          return (
            <TableFilterChip
              key={displayName || name}
              name={name}
              value={filterValues[name]}
              required={filterRequired}
              recommendation
              onClick={() => this.handleEditFilter(name)}
              innerRef={editingColumn === name ? this.handleEditFilterRef : undefined}
            />
          );
        })}
        {preventMoreFilters ? (
          undefined
        ) : (
          <Menu
            disableRestoreFocus={editDialogOpen}
            onClose={this.handleFilterClose}
            open={open}
            button={
              renderButtons ? (
                <Button id="cla-btn-add-filter" onClick={this.handleFilterOpen} size="small">
                  <AddIcon id="cla-icon-add-filter" />
                  add filter
                </Button>
              ) : (
                <div />
              )
            }
          >
            <ListSubheader id="cla-filter-subheader" key="recommended">
              Filter by:
            </ListSubheader>
            ,
            {!_.isEmpty(recommended)
              ? [
                  <ListSubheader key="recommended">Recommended</ListSubheader>,
                  ...recommended.map(columnName => (
                    <MenuItem key={columnName} onClick={() => this.handleEditFilter(columnName)}>
                      {columns[columnName].displayName || columnName}
                    </MenuItem>
                  )),
                ]
              : undefined}
            {!_.isEmpty(otherColumns)
              ? [
                  !_.isEmpty(recommended) ? <ListSubheader key="other">Other filters</ListSubheader> : undefined,
                  ...otherColumns.map(columnName => (
                    <MenuItem
                      key={columnName}
                      id={`cla-menu-itm-${stringToDashed(columnName)}`}
                      onClick={() => this.handleEditFilter(columnName)}
                    >
                      {columns[columnName].displayName || columnName}
                    </MenuItem>
                  )),
                ]
              : undefined}
          </Menu>
        )}
        {clearable && renderButtons ? (
          <IconButton className={clearButton} onClick={this.handleClearFilter}>
            <ClearIcon id="cla-btn-clear-filter-bar" />
          </IconButton>
        ) : (
          undefined
        )}
      </div>
    );
  }
}

export default withStyles(styles)(TableFilter);
